import {
  Checkbox,
  CrudMode,
  DataDisplay,
  formatBoolean,
  formatCurrency,
  formatDate,
  formatPercent,
  Grid,
  GridBreak,
  pn,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import { DatePicker, Select } from "components";
import { NonPersonnel, NonPersonnelEditResource, NonPersonnelViewResource } from "generated/openApi";

import produce from "immer";
import "moment-timezone";
import { useEffect } from "react";
import ApiService from "services/apiService";

export const MainInfo = (props: {
  id: number;
  crudMode: CrudMode;
  nonPersonnel: NonPersonnel;
  setNonPersonnel: (value: NonPersonnel) => void;
  viewResource: NonPersonnelViewResource;
  editResource: NonPersonnelEditResource;
  validationErrors: ValidationErrors;
}) => {
  const { breakpointsRef, smallFieldBreakpoints } = useElementBreakpoints();
  const properties = props.viewResource.properties || [];

  const filteredSubPartners =
    props.viewResource.subPartners?.filter((x) => x.partnerId === props.nonPersonnel.partnerId) || [];

  useEffect(() => {
    if (props.id > 0) {
      ApiService.contributorEmployments.get(props.id).then((response) => {
        const currentEmployment = response;
        const annualSalary = currentEmployment ? currentEmployment.annualSalary : undefined;
        const annualPercent = currentEmployment ? currentEmployment.annualPercent : undefined;
        props.setNonPersonnel(
          produce(props.nonPersonnel, (draft) => {
            draft.annualSalary = annualSalary;
            draft.annualPercent = annualPercent;
          })
        );
      });
    }
  }, [props.id]);

  const isMatchField = () => {
    const propertyName = pn<NonPersonnel>("isMatch");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.nonPersonnel.isMatch)}</DataDisplay>
    ) : (
      <Checkbox
        label={property?.displayName}
        checked={props.nonPersonnel.isMatch}
        onChange={(e) =>
          props.setNonPersonnel(
            produce(props.nonPersonnel, (draft) => {
              draft.isMatch = e.target.checked;
            })
          )
        }
      />
    );
  };

  const dateField = () => {
    const propertyName = pn<NonPersonnel>("date");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatDate(props.nonPersonnel.date)}</DataDisplay>
    ) : (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.nonPersonnel.date}
        onChange={(value) =>
          props.setNonPersonnel(
            produce(props.nonPersonnel, (draft) => {
              draft.date = value;
            })
          )
        }
      />
    );
  };

  const partnerField = () => {
    const propertyName = pn<NonPersonnel>("partnerId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.nonPersonnel.partner?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.partners?.find((x) => x.id === props.nonPersonnel.partnerId)}
        options={props.editResource.partners}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setNonPersonnel(
            produce(props.nonPersonnel, (draft) => {
              draft.partnerId = value?.id;
            })
          )
        }
      />
    );
  };

  const subPartnerField = () => {
    const propertyName = pn<NonPersonnel>("subPartnerId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.nonPersonnel.subPartner?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[pn<NonPersonnel>("subPartnerId")]}
        value={filteredSubPartners?.find((x) => x.id === props.nonPersonnel.subPartnerId)}
        options={filteredSubPartners}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setNonPersonnel(
            produce(props.nonPersonnel, (draft) => {
              draft.subPartnerId = value?.id;
            })
          )
        }
      />
    );
  };

  const reviewField = () => {
    const propertyName = pn<NonPersonnel>("reviewId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.nonPersonnel.review?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.matchReviews?.find((x) => x.id === props.nonPersonnel.reviewId)}
        options={props.editResource.matchReviews}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setNonPersonnel(
            produce(props.nonPersonnel, (draft) => {
              draft.reviewId = value?.id;
            })
          )
        }
      />
    );
  };

  const contributorField = () => {
    const propertyName = pn<NonPersonnel>("contributorId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        {props.nonPersonnel.contributor && (
          <>
            {props.nonPersonnel.contributor.lastName}, {props.nonPersonnel.contributor.firstName}
          </>
        )}
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.nonPersonnel.contributor}
        getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
        onChange={(e, value) => {
          props.setNonPersonnel(
            produce(props.nonPersonnel, (draft) => {
              draft.contributorId = value?.id;
              draft.contributor = value;
            })
          );
        }}
        fetchFunction={(searchString: string) => ApiService.search.contributors(searchString)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const annualSalaryField = () => (
    <DataDisplay label={properties.find((x) => x.propertyName === pn<NonPersonnel>("annualSalary"))?.displayName}>
      {formatCurrency(props.nonPersonnel.annualSalary)}
    </DataDisplay>
  );

  const annualPercentField = () => (
    <DataDisplay label={properties.find((x) => x.propertyName === pn<NonPersonnel>("annualPercent"))?.displayName}>
      {formatPercent(props.nonPersonnel.annualPercent)}
    </DataDisplay>
  );

  const staffField = () => {
    const propertyName = pn<NonPersonnel>("staffId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        {props.nonPersonnel.staff && (
          <>
            {props.nonPersonnel.staff?.lastName}, {props.nonPersonnel.staff?.firstName}
          </>
        )}
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.staffs?.find((x) => x.id === props.nonPersonnel.staffId)}
        options={props.editResource.staffs}
        getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
        onChange={(e, value) =>
          props.setNonPersonnel(
            produce(props.nonPersonnel, (draft) => {
              draft.staffId = value?.id;
            })
          )
        }
      />
    );
  };

  return (
    <Grid ref={breakpointsRef} container spacing="sm">
      <Grid item xs={smallFieldBreakpoints}>
        {isMatchField()}
      </Grid>

      <GridBreak />

      <Grid item xs={smallFieldBreakpoints}>
        {dateField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {partnerField()}
      </Grid>

      {filteredSubPartners.length > 0 && (
        <Grid item xs={smallFieldBreakpoints}>
          {subPartnerField()}
        </Grid>
      )}

      <Grid item xs={smallFieldBreakpoints}>
        {reviewField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {contributorField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {staffField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {annualSalaryField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {annualPercentField()}
      </Grid>
    </Grid>
  );
};
